import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaPlus,
  FaTimes,
  FaChartLine,
  FaBarcode,
  FaCreditCard,
  FaCashRegister,
  FaSignOutAlt
} from 'react-icons/fa';
import './FloatingNavModal.css';

const FloatingNavModal = ({ currentPage = 'dashboard' }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const navigationOptions = [
    {
      id: 'analytics',
      title: 'Analytics',
      description: 'View sales analytics and reports',
      icon: FaChartLine,
      path: '/collection/paid-summary',
      color: 'primary',
      disabled: currentPage === 'analytics'
    },
    {
      id: 'barcode',
      title: 'Barcode Payment',
      description: 'Process payments with barcode scanning',
      icon: FaBarcode,
      path: '/collection/barcode-payment',
      color: 'warning',
      disabled: currentPage === 'barcode'
    },
    {
      id: 'manual',
      title: 'Manual Payment',
      description: 'Process manual payments',
      icon: FaCreditCard,
      path: '/collection/manual-payment',
      color: 'info',
      disabled: currentPage === 'manual'
    },
    {
      id: 'counter-close',
      title: 'Counter Close',
      description: 'Daily counter reconciliation and closing',
      icon: FaCashRegister,
      path: '/collection/counter-close',
      color: 'danger',
      disabled: currentPage === 'counter-close'
    },
    {
      id: 'logout',
      title: 'Logout',
      description: 'Sign out from the system',
      icon: FaSignOutAlt,
      action: 'logout',
      color: 'secondary',
      disabled: false
    }
  ];

  const handleNavigation = (path) => {
    setIsModalOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    setIsModalOpen(false);
    
    // Clear all authentication data
    localStorage.removeItem('token');
    localStorage.removeItem('userid');
    localStorage.removeItem('uname');
    localStorage.removeItem('usertype');
    localStorage.removeItem('counterCloseStatus');
    
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('userid');
    sessionStorage.removeItem('uname');
    sessionStorage.removeItem('usertype');
    sessionStorage.removeItem('counterCloseStatus');
    
    // Navigate to login page
    navigate('/');
  };

  const handleOptionClick = (option) => {
    if (option.disabled) return;
    
    if (option.action === 'logout') {
      handleLogout();
    } else if (option.path) {
      handleNavigation(option.path);
    }
  };

  const getColorStyle = (color) => {
    const colors = {
      primary: 'linear-gradient(135deg, #007bff, #0056b3)',
      warning: 'linear-gradient(135deg, #ffc107, #e0a800)',
      info: 'linear-gradient(135deg, #17a2b8, #138496)',
      success: 'linear-gradient(135deg, #28a745, #1e7e34)',
      danger: 'linear-gradient(135deg, #dc3545, #c82333)',
      secondary: 'linear-gradient(135deg, #6c757d, #545b62)'
    };
    return colors[color] || colors.primary;
  };

  return (
    <>
      {/* Floating Button */}
      <div className="floating-nav-button">
        <button
          className="nav-toggle-btn"
          onClick={() => setIsModalOpen(true)}
          title="Navigation Menu"
        >
          <FaPlus className={`nav-icon ${isModalOpen ? 'rotated' : ''}`} />
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="nav-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="nav-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="nav-modal-header">
              <h5 className="modal-title">
                <FaPlus className="me-2" />
                Navigation Menu
              </h5>
              <button
                className="close-btn"
                onClick={() => setIsModalOpen(false)}
                title="Close"
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal Body */}
            <div className="nav-modal-body">
              <div className="nav-grid">
                {navigationOptions.map((option) => {
                  const IconComponent = option.icon;
                  return (
                    <button
                      key={option.id}
                      className={`nav-option ${option.disabled ? 'disabled' : ''}`}
                      onClick={() => handleOptionClick(option)}
                      disabled={option.disabled}
                      style={{
                        background: option.disabled 
                          ? '#e9ecef' 
                          : getColorStyle(option.color)
                      }}
                    >
                      <div className="nav-option-icon">
                        <IconComponent />
                      </div>
                      <div className="nav-option-content">
                        <h6 className="nav-option-title">
                          {option.title}
                          {option.disabled && <span className="current-badge">Current</span>}
                        </h6>
                        <p className="nav-option-description">
                          {option.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="nav-modal-footer">
              <small className="text-muted">
                Quick access to all payment and analytics features
              </small>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingNavModal;